import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  planned: { bg: "rgba(59,130,246,0.15)", text: "#60a5fa", label: "PLANNED" },
  active: { bg: "rgba(34,197,94,0.15)", text: "#4ade80", label: "ACTIVE" },
  paused: { bg: "rgba(234,179,8,0.15)", text: "#facc15", label: "PAUSED" },
  error: { bg: "rgba(239,68,68,0.15)", text: "#f87171", label: "ERROR" },
};

export function MarketingPanel() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/marketing/overview`);
      setOverview(res.data);
    } catch (e) {
      console.error("Marketing overview error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    const interval = setInterval(fetchOverview, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/marketing/generate-all`, {}, { timeout: 120000 });
      await fetchOverview();
    } catch (e) {
      console.error("Generate campaigns error:", e);
    } finally {
      setGenerating(false);
    }
  };

  const handleViewDetail = async (campaignId) => {
    if (selectedCampaign === campaignId) {
      setSelectedCampaign(null);
      setDetail(null);
      return;
    }
    setSelectedCampaign(campaignId);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/marketing/campaign/${campaignId}`);
      setDetail(res.data);
    } catch (e) {
      console.error("Campaign detail error:", e);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="marketing-panel-loading" style={{ padding: 16, color: "#71717a", fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}>
        Cargando marketing...
      </div>
    );
  }

  return (
    <div data-testid="marketing-panel" style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 8, height: 8, borderRadius: "50%",
            background: overview?.api_connected ? "#4ade80" : "#facc15",
            boxShadow: overview?.api_connected ? "0 0 6px rgba(74,222,128,0.5)" : "0 0 6px rgba(250,204,21,0.4)",
          }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#e4e4e7", fontFamily: "'IBM Plex Mono', monospace" }}>
            MEDIA PLAN
          </span>
          {overview?.google_ads_customer_id && (
            <span style={{ fontSize: 9, color: "#71717a", fontFamily: "'IBM Plex Mono', monospace" }}>
              ID: {overview.google_ads_customer_id}
            </span>
          )}
        </div>
        <button
          data-testid="generate-all-campaigns-btn"
          onClick={handleGenerateAll}
          disabled={generating}
          style={{
            padding: "4px 10px", fontSize: 9, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
            textTransform: "uppercase", letterSpacing: "0.1em",
            background: generating ? "rgba(255,255,255,0.05)" : "rgba(59,130,246,0.2)",
            color: generating ? "#71717a" : "#60a5fa",
            border: `1px solid ${generating ? "rgba(255,255,255,0.06)" : "rgba(59,130,246,0.3)"}`,
            borderRadius: 4, cursor: generating ? "wait" : "pointer",
            transition: "all 0.2s",
          }}
        >
          {generating ? "GENERANDO..." : "GENERAR CAMPANAS"}
        </button>
      </div>

      {/* Stats Row */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1,
        padding: "0", borderBottom: "1px solid rgba(255,255,255,0.06)",
        flexShrink: 0,
      }}>
        {[
          { label: "Campanas", value: overview?.total_campaigns || 0, color: "#e4e4e7" },
          { label: "Planned", value: overview?.planned || 0, color: "#60a5fa" },
          { label: "Budget/dia", value: `$${overview?.total_daily_budget_usd || 0}`, color: "#facc15" },
          { label: "Est. Clicks", value: overview?.total_est_daily_clicks || 0, color: "#4ade80" },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: "8px 10px", textAlign: "center",
            background: "rgba(255,255,255,0.02)",
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: stat.color, fontFamily: "'Chivo', sans-serif" }}>
              {stat.value}
            </div>
            <div style={{ fontSize: 8, color: "#71717a", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'IBM Plex Mono', monospace" }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* API Status Banner */}
      {!overview?.api_connected && (
        <div style={{
          padding: "6px 14px", fontSize: 9, fontFamily: "'IBM Plex Mono', monospace",
          background: "rgba(234,179,8,0.08)", color: "#facc15",
          borderBottom: "1px solid rgba(234,179,8,0.15)",
          flexShrink: 0,
        }}>
          Google Ads API pendiente — Campanas generadas listas para push
        </div>
      )}

      {/* Campaign List */}
      <div data-testid="campaigns-list" style={{ flex: 1, overflow: "auto" }}>
        {(!overview?.campaigns || overview.campaigns.length === 0) ? (
          <div style={{
            padding: 20, textAlign: "center", color: "#52525b", fontSize: 11,
            fontFamily: "'IBM Plex Mono', monospace",
          }}>
            Sin campanas generadas.
            <br />
            <span style={{ fontSize: 9, color: "#3f3f46" }}>
              Usa "GENERAR CAMPANAS" para crear media plan
            </span>
          </div>
        ) : (
          overview.campaigns.map((campaign) => {
            const status = STATUS_COLORS[campaign.status] || STATUS_COLORS.planned;
            const isSelected = selectedCampaign === campaign.id;
            return (
              <div key={campaign.id}>
                <div
                  data-testid={`campaign-row-${campaign.id}`}
                  onClick={() => handleViewDetail(campaign.id)}
                  style={{
                    padding: "8px 14px", cursor: "pointer",
                    borderBottom: "1px solid rgba(255,255,255,0.04)",
                    background: isSelected ? "rgba(59,130,246,0.08)" : "transparent",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#e4e4e7", fontFamily: "'IBM Plex Mono', monospace" }}>
                      {campaign.brand_name}
                    </span>
                    <span style={{
                      fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 3,
                      background: status.bg, color: status.text, letterSpacing: "0.08em",
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}>
                      {status.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 9, color: "#a1a1aa", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 4 }}>
                    {campaign.campaign_name}
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 9, color: "#71717a", fontFamily: "'IBM Plex Mono', monospace" }}>
                    <span>{campaign.headlines_count} titulares</span>
                    <span>{campaign.keywords_count} keywords</span>
                    <span>${campaign.daily_budget_usd}/dia</span>
                    <span>~{campaign.est_daily_clicks} clicks</span>
                    <span>ROAS: {campaign.est_roas}x</span>
                  </div>
                </div>

                {/* Expanded Detail */}
                {isSelected && (
                  <div style={{
                    padding: "10px 14px",
                    background: "rgba(59,130,246,0.04)",
                    borderBottom: "1px solid rgba(59,130,246,0.1)",
                  }}>
                    {detailLoading ? (
                      <div style={{ fontSize: 10, color: "#71717a", fontFamily: "'IBM Plex Mono', monospace" }}>
                        Cargando detalle...
                      </div>
                    ) : detail ? (
                      <CampaignDetail data={detail} />
                    ) : null}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


function CampaignDetail({ data }) {
  const cd = data.campaign_data || {};
  const rsa = cd.responsive_search_ad || {};
  const ag = cd.ad_group || {};
  const metrics = cd.estimated_metrics || {};

  return (
    <div data-testid="campaign-detail" style={{ fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", color: "#a1a1aa" }}>
      {/* Headlines */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
          Titulares ({rsa.headlines?.length || 0})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(rsa.headlines || []).map((h, i) => (
            <span key={i} style={{
              padding: "2px 6px", borderRadius: 3, fontSize: 9,
              background: "rgba(59,130,246,0.1)", color: "#93c5fd",
              border: "1px solid rgba(59,130,246,0.15)",
            }}>
              {h}
            </span>
          ))}
        </div>
      </div>

      {/* Descriptions */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#a78bfa", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
          Descripciones ({rsa.descriptions?.length || 0})
        </div>
        {(rsa.descriptions || []).map((d, i) => (
          <div key={i} style={{ padding: "3px 0", color: "#c4b5fd", fontSize: 9 }}>
            {d}
          </div>
        ))}
      </div>

      {/* Keywords */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#4ade80", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
          Keywords ({ag.keywords?.length || 0})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(ag.keywords || []).map((kw, i) => {
            const matchColor = kw.match_type === "EXACT" ? "#4ade80" : kw.match_type === "PHRASE" ? "#facc15" : "#a1a1aa";
            return (
              <span key={i} style={{
                padding: "2px 6px", borderRadius: 3, fontSize: 9,
                background: `${matchColor}15`, color: matchColor,
                border: `1px solid ${matchColor}25`,
              }}>
                {kw.match_type === "EXACT" ? `[${kw.text}]` : kw.match_type === "PHRASE" ? `"${kw.text}"` : kw.text}
              </span>
            );
          })}
        </div>
      </div>

      {/* Estimated Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        {[
          { label: "Impresiones/dia", value: metrics.daily_impressions },
          { label: "Clicks/dia", value: metrics.daily_clicks },
          { label: "CTR", value: `${metrics.estimated_ctr_pct}%` },
          { label: "CPC", value: `$${metrics.estimated_cpc_usd}` },
          { label: "Conv/dia", value: metrics.estimated_daily_conversions },
          { label: "ROAS", value: `${metrics.estimated_roas}x` },
        ].map((m, i) => (
          <div key={i} style={{
            padding: "4px 6px", borderRadius: 3,
            background: "rgba(255,255,255,0.03)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#e4e4e7" }}>{m.value}</div>
            <div style={{ fontSize: 7, color: "#52525b", textTransform: "uppercase", letterSpacing: "0.08em" }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* Optimization Notes */}
      {cd.optimization_notes && (
        <div style={{ marginTop: 8, padding: "6px 8px", borderRadius: 3, background: "rgba(255,255,255,0.02)", fontSize: 9, color: "#71717a", lineHeight: 1.4 }}>
          {cd.optimization_notes}
        </div>
      )}
    </div>
  );
}
